import Backbone from 'backbone';
import React from 'react';

import ReactDOM from 'react-dom';
import _ from 'underscore';

import * as vo from 'components/VisibilityOptions';
import * as Perf from 'performance/Perf';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getDedicatedGeneralNoteViewProps, getSharedNoteViewProps} from 'notebook/notes/tabs/NoteViewProps';
import PublishingTemplateNoteView from 'notebook/notes/tabs/PublishingTemplateNoteView';
import PublishingSubtemplateNoteView from 'notebook/notes/tabs/PublishingSubtemplateNoteView';
import {EditorPropertiesContext, NoteContext} from 'notebook/notes/core-ui/NoteContext';
import GeneralNoteView from 'notebook/notes/tabs/GeneralNoteView';
import {EntryNoteView} from 'notebook/notes/tabs/NoteViews';
import SubtemplateNoteView, {SUBTEMPLATE_VIEW_PRINT_OPTIONS} from 'notebook/notes/tabs/SubtemplateNoteView';
import {
  TemplateNoteView,
  TEMPLATE_VIEW_PRINT_OPTIONS,
  TEMPLATE_VIEW_PRINT_OPTIONS_WITHOUT_PROTOCOLS,
} from 'notebook/notes/tabs/TemplateNoteView';
import * as ReduxDOMRender from 'redux-stores/ReduxDOMRender';
import APIUtil from 'util/APIUtil';
import StageEntryNoteView from 'workflows/stage-entries/StageEntryNoteView';
import {trackDescriptionTabViewIfValidSequence} from 'molbio/SequenceAnalyticsHelper';

function __guard__(value, transform) {
  return value != null ? transform(value) : undefined;
}

const SCROLLING_PERF_INTERVAL = 1 * 60 * 1000;

export default class NoteTabView extends Backbone.View {
  static initClass() {
    this.prototype.className = 'buffer-tab buffer-tab--no-padding';
  }

  initialize() {
    // Bind method to `this` so it can be threaded into React components
    this.noteOnLoad = this.noteOnLoad.bind(this);
    this._getScrollingPerfMetadata = this._getScrollingPerfMetadata.bind(this);
    trackDescriptionTabViewIfValidSequence(this.model.get('model'));
  }

  render() {
    this.listenTo(this.model.get('model'), 'change', this.rerender);
    this.listenTo(this.model.get('displayOptions'), 'redraw', this.rerender);
    this.rerender();
    return this;
  }

  noteOnLoad() {
    const scrollingPerf = Perf.start(
      `${APIUtil.apiIdToType(this.model.get('fileId'))}_note_view_scroll`,
      {enabled: true, getMetadata: this._getScrollingPerfMetadata},
      {
        interval: SCROLLING_PERF_INTERVAL,
        aggregations: [Perf.Aggregation.MEDIAN, Perf.Aggregation.MAX],
      }
    );
    Perf.markStart(scrollingPerf);
    // Keep scroll top value between rerenders.
    const scroller = this.$('.noteEditor-scrollContainer');
    scroller.scroll(() => this.model.set({scrollTop: scroller.scrollTop()}, {silent: true}));
    // We have to defer this, because we need to wait till the tab is added
    // to the DOM. Setting scroll top before the DOM node is in the document
    // doesn't work.
    _.defer(() => {
      scroller.scrollTop(this.model.get('scrollTop'));
      Perf.markEnd(scrollingPerf);
    });
  }

  afterAttach() {
    this.rerender();
  }

  getRootFolderId() {
    const {attributes} = this.model.get('model');
    return attributes?.entry?.folder?.source?.id ?? null;
  }

  getTemplateCollectionId() {
    const {attributes} = this.model.get('model');
    return attributes.template?.templateCollection?.id ?? null;
  }

  _getScrollingPerfMetadata() {
    const {attributes} = this.model.get('model');
    if (attributes && attributes.entry) {
      return {
        entryId: attributes.entry.entryId,
      };
    }
    return {};
  }

  rerender() {
    const noteProps = {
      ...getSharedNoteViewProps(this.model),
      onLoad: this.noteOnLoad,
    };
    const showFormulas = this.model.get('displayOptions').getOption(vo.ELN_SHOW_FORMULAS.name).get('visible');

    const highlightCustomColumns = this.model
      .get('displayOptions')
      .getOption(vo.ELN_HIGHLIGHT_CUSTOM_COLUMNS.name)
      .get('visible');
    const banner = this.model.get('model').getBannerText();
    // eslint-disable-next-line no-restricted-syntax
    _.extend(
      noteProps,
      (() => {
        switch (this.model.get('model').fileType()) {
          case ModelTypeConstants.ENTRY:
            return {
              banner,
              note: __guard__(this.model.get('model').proxied(), (entry) => entry.note),
              structuredTableMappings: __guard__(
                this.model.get('model').proxied(),
                (entry) => entry.structuredTableMappings
              ),
              dirty: this.model.get('model').isDirty(),
              isLatest: this.model.get('model').isLatest(),
              title: __guard__(this.model.get('model').proxied(), (entry) => entry.name),
              parentType: 'Entry',
              review: this.model.get('model').review(),
              printOptions:
                this.model.get('model').get('entry').entryProtocols.length > 0
                  ? EntryNoteView.PRINT_OPTIONS
                  : EntryNoteView.PRINT_OPTIONS_WITHOUT_PROTOCOLS,
              collaboratorIds: this.model.get('model').collaboratorIds(),
              templateVersionId: this.model.get('model').templateVersionId(),
              templateUrl: this.model.get('model').templateUrl(),
              templateVersionTimestamp: this.model.get('model').templateVersionTimestamp(),
              lockedFromExternalChange: this.model.get('model').lockedFromExternalChange(),
              authors: this.model.get('model').authors(),
              focusedTableProvenanceUuid: this.model.get('model').get('entry').focusedTableProvenanceUuid,
              focusedTableViewMode: this.model.get('model').get('entry').focusedTableViewMode,
            };
          case ModelTypeConstants.STAGE_ENTRY:
            return {
              banner,
              note: __guard__(
                this.model.get('model').proxied(),
                (stageEntryDoc) => stageEntryDoc.entryVersion.note
              ),
              isLatest: this.model.get('model').isLatest(),
              title: __guard__(this.model.get('model').proxied(), (stageEntryDoc) => stageEntryDoc.name),
              review: this.model.get('model').review(),
              parentType: 'Workflow Entry',
              printOptions:
                this.model.get('model').get('stageEntryDocument').entryVersion.entryProtocols.length > 0
                  ? EntryNoteView.PRINT_OPTIONS
                  : EntryNoteView.PRINT_OPTIONS_WITHOUT_PROTOCOLS,
              lockedFromExternalChange: this.model.get('model').lockedFromExternalChange(),
            };
          case ModelTypeConstants.TEMPLATE:
            return {
              banner,
              isUnsaved: this.model.get('model').isUnsaved(),
              note: __guard__(
                this.model.get('model').proxied(),
                (template) => template.templateVersion.entryVersion.note
              ),
              structuredTableMappings: __guard__(
                this.model.get('model').proxied(),
                (template) => template.templateVersion.entryVersion.structuredTableMappings
              ),
              isLatest: this.model.get('model').isLatest(),
              title: __guard__(this.model.get('model').proxied(), (template) => template.name),
              parentType: 'Template',
              printOptions:
                this.model.get('model').get('template').templateVersion.entryVersion.entryProtocols.length > 0
                  ? TEMPLATE_VIEW_PRINT_OPTIONS
                  : TEMPLATE_VIEW_PRINT_OPTIONS_WITHOUT_PROTOCOLS,
              lockedFromExternalChange: this.model.get('model').lockedFromExternalChange(),
              focusedTableProvenanceUuid: this.model.get('model').get('template').focusedTableProvenanceUuid,
              focusedTableViewMode: this.model.get('model').get('template').focusedTableViewMode,
              openVersionApiId: this.model.get('model').openVersionApiId(),
              openVersionModifiedAt: this.model.get('model').openVersionModifiedAt(),
            };
          case ModelTypeConstants.SUBTEMPLATE:
            return {
              banner,
              note: __guard__(
                this.model.get('model').proxied(),
                (subtemplate) => subtemplate.subtemplateVersion.noteVersion
              ),
              isLatest: this.model.get('model').isLatest(),
              title: __guard__(this.model.get('model').proxied(), (subtemplate) => subtemplate.name),
              parentType: 'Sub-Template',
              printOptions: SUBTEMPLATE_VIEW_PRINT_OPTIONS,
              lockedFromExternalChange: this.model.get('model').lockedFromExternalChange(),
              openVersionApiId: this.model.get('model').openVersionApiId(),
              openVersionModifiedAt: this.model.get('model').openVersionModifiedAt(),
            };
          default:
            return {...getDedicatedGeneralNoteViewProps(this.model)};
        }
      })()
    );

    const NoteViewCls = (() => {
      switch (this.model.get('model').fileType()) {
        case ModelTypeConstants.ENTRY:
          return EntryNoteView;
        case ModelTypeConstants.STAGE_ENTRY:
          return StageEntryNoteView;
        case ModelTypeConstants.TEMPLATE:
          if (BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING) {
            return PublishingTemplateNoteView;
          }
          return TemplateNoteView;
        case ModelTypeConstants.SUBTEMPLATE:
          if (BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING) {
            return PublishingSubtemplateNoteView;
          }
          return SubtemplateNoteView;
        default:
          return GeneralNoteView;
      }
    })();
    ReduxDOMRender.renderToDOM(
      <NoteContext.Provider value={{note: noteProps.note}}>
        <EditorPropertiesContext.Provider
          value={{
            showFormulas,
            highlightCustomColumns,
            rootFolderId: this.getRootFolderId(),
            fileId: noteProps.fileId,
            templateCollectionId: this.getTemplateCollectionId(),
          }}
        >
          <NoteViewCls {...noteProps} />
        </EditorPropertiesContext.Provider>
      </NoteContext.Provider>,
      this.el
    );
  }

  /**
   * `Backbone.View.remove()` without cleaning up nested $.data usage.
   *
   * `Backbone.View.remove()` calls `this.$el.remove()` which calls `$.cleanData`. If this is called before
   * `$.tooltip('destroy')`, the tooltips will be stuck on the page. For robustness against cases where
   * the cleanup of children nodes is called after this, we avoid calling `$.cleanData()` altogether.
   *
   * Reference:
   *   - https://github.com/jashkenas/backbone/blob/1.1.2/backbone.js#L1034-L1040
   *   - https://github.com/benchling/aurelia/blob/fcdd6d9/packages/%40benchling-jquery/src/jquery.js#L5305-L5324
   *   - https://api.jquery.com/remove/
   *
   * TODO(BNCH-48627): Remove this hack once we migrate away from Bootstrap tooltips.
   */
  _removeWithoutCleanData() {
    this.$el.detach();
    this.stopListening();
  }

  remove() {
    Perf.stop(this.scrollingPerf);
    ReactDOM.unmountComponentAtNode(this.el);
    // If NoteTabView component is rendered within a React component, then the above unmount will be async,
    // so we defensively use `_removeWithoutCleanData` in place of `super.remove()`.
    this._removeWithoutCleanData();
  }
}
NoteTabView.initClass();
